
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Download, Upload, RefreshCw, CheckCircle, List, History } from 'lucide-react';
import { toast } from 'sonner';
import { createHistoryBackup, restoreHistoryBackupFromFile } from '@/services/backup/historyBackup';
import { motion } from 'framer-motion';
import HistoryBackupList from './HistoryBackupList';

const HistoryBackupPanel = () => {
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [showBackupList, setShowBackupList] = useState(false);
  const [backupCreated, setBackupCreated] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const [stats, setStats] = useState({
    adminActions: 0,
    teacherHistory: 0,
    studentHistory: 0,
    communicationLogs: 0
  });
  
  useEffect(() => {
    // Count admin actions
    const adminActionKeys = Object.keys(localStorage).filter(key => key.includes('admin_action_'));
    
    // Count teacher history
    const teacherHistoryKeys = Object.keys(localStorage).filter(key => key.includes('teacher_history_'));
    
    // Count student history
    const studentHistoryKeys = Object.keys(localStorage).filter(key => key.includes('student_history_'));
    
    // Count communication logs
    const communicationLogsData = localStorage.getItem('communication_logs');
    const communicationLogs = communicationLogsData ? JSON.parse(communicationLogsData).length : 0;
    
    setStats({
      adminActions: adminActionKeys.length,
      teacherHistory: teacherHistoryKeys.length,
      studentHistory: studentHistoryKeys.length,
      communicationLogs
    });
  }, []);
  
  const handleCreateBackup = async () => {
    if (Object.values(stats).every(v => v === 0)) {
      toast.warning('No history data to backup');
      return;
    }
    
    setIsCreatingBackup(true);
    setBackupCreated(false);
    
    try {
      await createHistoryBackup();
      setBackupCreated(true);
      setTimeout(() => setBackupCreated(false), 3000);
    } catch (error) {
      console.error('Failed to create history backup:', error);
      toast.error('Failed to create history backup');
    } finally {
      setIsCreatingBackup(false);
    }
  };
  
  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    setIsRestoringBackup(true);
    
    try {
      const result = await restoreHistoryBackupFromFile(file);
      if (result) {
        toast.success('History data restored successfully');
      }
    } catch (error) {
      console.error('Failed to restore history backup:', error);
      toast.error('Failed to restore history backup');
    } finally {
      setIsRestoringBackup(false);
      // Clear the input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const toggleBackupList = () => {
    setShowBackupList(!showBackupList);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-lg">
          <History className="h-5 w-5 mr-2" />
          History & Records Backup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground mb-4">
          {Object.values(stats).reduce((a, b) => a + b, 0) === 0 
            ? 'No history data available for backup' 
            : `${Object.values(stats).reduce((a, b) => a + b, 0)} history records available for backup`}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-2 gap-2 mb-4 text-xs text-muted-foreground">
          <div className="flex items-center">
            <span>Admin Actions: {stats.adminActions}</span>
          </div>
          <div className="flex items-center">
            <span>Teacher Records: {stats.teacherHistory}</span>
          </div>
          <div className="flex items-center">
            <span>Student Records: {stats.studentHistory}</span>
          </div>
          <div className="flex items-center">
            <span>Communication Logs: {stats.communicationLogs}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Button
              className="w-full h-14 flex items-center justify-center space-x-2"
              onClick={handleCreateBackup}
              disabled={isCreatingBackup || Object.values(stats).every(v => v === 0)}
              variant={backupCreated ? "secondary" : "default"}
            >
              {isCreatingBackup ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  <span>Creating Backup...</span>
                </>
              ) : backupCreated ? (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span>Backup Created!</span>
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  <span>Create History Backup</span>
                </>
              )}
            </Button>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Button
              className="w-full h-14 flex items-center justify-center space-x-2"
              onClick={handleSelectFile}
              disabled={isRestoringBackup}
              variant="outline"
            >
              {isRestoringBackup ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  <span>Restoring Backup...</span>
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5 mr-2" />
                  <span>Restore from File</span>
                </>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".json"
                className="hidden"
              />
            </Button>
          </motion.div>
        </div>
        
        <Button
          variant="ghost"
          className="w-full mt-2"
          onClick={toggleBackupList}
        >
          <List className="h-4 w-4 mr-2" />
          {showBackupList ? "Hide Backup History" : "View Backup History"}
        </Button>
        
        {showBackupList && (
          <HistoryBackupList />
        )}
      </CardContent>
    </Card>
  );
};

export default HistoryBackupPanel;
